import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  PointOfInterest,
  PointOfInterestTemplate,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  UnitsEnum,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import {
  BLISTER_HEIGHT_LABEL,
  BLISTER_TYPE,
  NOT_APPLICABLE_LABEL,
  PAINT_BLOCK_LABEL,
  PIT_DEPTH_LABEL,
  SUMMARY_VALUE_ICONS,
  UNCERTAIN_PROFILE_LABEL,
} from '@/constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { useBlisterFieldConfig } from '@/hooks/useBlisterFieldConfig';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';

export const usePoiTemplateFields = ({ data }: { data: PointOfInterest }) => {
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const blisterFieldConfig = useBlisterFieldConfig({ type: data.type });

  const { data: allPointOfInterestTemplates } = useFetchTemplatesByType();

  const isAutoPOI = data.isAutoGenerated || !!data.blister;

  const templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined = useMemo(() => {
    return allPointOfInterestTemplates?.allPointOfInterestTemplate.find(
      ({ id, key }) =>
        id === data.template?.id ||
        id === data.templateId ||
        (isAutoPOI && (key === 'blister' || key === 'pit'))
    );
  }, [allPointOfInterestTemplates, data]);

  const templateFields = useMemo(() => {
    return (
      templateOfSelectedPoi?.fields?.filter(
        (field) => field.name !== 'Comments' && field.displayInReview
      ) || []
    );
  }, [templateOfSelectedPoi]);

  const getFieldValue = useCallback(
    (field: { key?: string | null }) =>
      (field?.key ? data.templateFieldData?.[field.key] : undefined) ?? NOT_APPLICABLE_LABEL,
    [data.templateFieldData]
  );

  const templateFieldsWithValues: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
    valueIcon?: string;
    displayStyle?: TableCellStyleEnum | undefined;
  }> = useMemo(() => {
    const height = data.deckRelativeHeight;
    const heightValue =
      localiseMeasurement({
        unitSystem: unitConversionSystem,
        value: height,
        sourceUnit: UnitsEnum.Meter,
        roundToDecimal: 1,
      }) ?? NOT_APPLICABLE_LABEL;

    const additionalFieldsData = [
      { label: 'Height from deck', value: heightValue ?? NOT_APPLICABLE_LABEL },
      {
        label: PAINT_BLOCK_LABEL,
        value: data.paintRegion?.name ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Deck Name',
        value: data.paintRegion?.deck?.name ?? NOT_APPLICABLE_LABEL,
      },
    ];

    const mappedTemplateFields =
      templateFields?.map((field) => {
        const value = getFieldValue(field);
        const matchingIcon = SUMMARY_VALUE_ICONS.find(
          (iconConfig) => iconConfig.key === field.key && iconConfig.value === value
        );
        return {
          value,
          label: field?.name || NOT_APPLICABLE_LABEL,
          type: field?.type,
          displayStyle: field.displayConfig?.type,
          ...(matchingIcon && { valueIcon: matchingIcon.icon }),
        };
      }) || [];

    if (data.blister) {
      const { maxHeight, pdrHeight, wallLoss, residualCorrosionAllowance } = data.blister || {};
      const label =
        blisterFieldConfig.maxHeight?.name ||
        (data.type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL);
      const blisterValue =
        maxHeight === 0
          ? UNCERTAIN_PROFILE_LABEL
          : localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: maxHeight || undefined,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              fallbackLabel: UNCERTAIN_PROFILE_LABEL,
              showUnitLabel: true,
            }) ?? UNCERTAIN_PROFILE_LABEL;

      const PdrHeightValue =
        maxHeight === 0
          ? UNCERTAIN_PROFILE_LABEL
          : localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: pdrHeight || undefined,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: true,
            });

      const blisterFields = [
        // Max Height field
        ...(!blisterFieldConfig.maxHeight?.hidden
          ? [
              {
                value: blisterValue,
                label: `${label}`,
              },
            ]
          : []),
        // Residual Corrosion Allowance field
        ...(!blisterFieldConfig.residualCorrosionAllowance?.hidden
          ? [
              {
                value:
                  residualCorrosionAllowance?.value === null ||
                  residualCorrosionAllowance?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem: unitConversionSystem,
                        value: residualCorrosionAllowance.value,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                        showUnitLabel: true,
                      }) +
                      (residualCorrosionAllowance?.percentage !== null &&
                      residualCorrosionAllowance?.percentage !== undefined
                        ? formatPercentage(residualCorrosionAllowance.percentage)
                        : ''),
                label:
                  blisterFieldConfig.residualCorrosionAllowance?.name ||
                  'Residual corrosion allowance',
              },
            ]
          : []),
        // PDR Height field
        ...(pdrHeight && !blisterFieldConfig.pdrHeight?.hidden
          ? [
              {
                value: PdrHeightValue,
                label: blisterFieldConfig.pdrHeight?.name || 'PDR Height',
              },
            ]
          : []),
        // Wall Loss field
        ...(!blisterFieldConfig.wallLoss?.hidden
          ? [
              {
                value:
                  wallLoss?.value === null || wallLoss?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem: unitConversionSystem,
                        value: wallLoss.value,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                        showUnitLabel: true,
                      }) +
                      (wallLoss?.percentage !== null && wallLoss?.percentage !== undefined
                        ? formatPercentage(wallLoss.percentage)
                        : ''),
                label: blisterFieldConfig.wallLoss?.name || 'Wall loss est',
              },
            ]
          : []),
        {
          value: data.blister?.measurementConfidence ?? NOT_APPLICABLE_LABEL,
          label: 'Measurement Confidence',
        },
        {
          value: data.blister?.distanceConfidence ?? NOT_APPLICABLE_LABEL,
          label: 'Distance Confidence',
        },
      ];

      return [...blisterFields, ...additionalFieldsData, ...mappedTemplateFields];
    }

    return [...mappedTemplateFields, ...additionalFieldsData];
  }, [
    data.deckRelativeHeight,
    data.paintRegion?.name,
    data.paintRegion?.deck?.name,
    data.blister,
    data.type,
    unitConversionSystem,
    templateFields,
    data.paintRegion?.deck?.name,
    blisterFieldConfig,
  ]);

  return {
    templateFieldsWithValues,
    templateOfSelectedPoi,
  };
};
